import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, adminApi } from "../../lib/api";
import { Field } from "./ImageField";

const CATEGORIES = ["General Rules", "Roleplay Rules", "Combat & Death", "Crime & Heists", "Organizations", "Properties", "Other - Misc."];

export const RulesEditor = () => {
  const [groups, setGroups] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/rules");
    setGroups(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = (id, key, val) => setGroups((p) => p.map((g) => (g.id === id ? { ...g, [key]: val } : g)));
  const patchItem = (id, idx, val) => setGroups((p) => p.map((g) => (g.id === id ? { ...g, items: g.items.map((it, i) => (i === idx ? val : it)) } : g)));
  const addItem = (id) => setGroups((p) => p.map((g) => (g.id === id ? { ...g, items: [...g.items, "New rule"] } : g)));
  const removeItem = (id, idx) => setGroups((p) => p.map((g) => (g.id === id ? { ...g, items: g.items.filter((_, i) => i !== idx) } : g)));

  const add = async () => {
    try { await adminApi.post("/rules", { cat: "New Category", items: ["New rule"] }); load(); toast.success("Category added"); }
    catch { toast.error("Add failed"); }
  };
  const save = async (g) => {
    try { await adminApi.put(`/rules/${g.id}`, g); toast.success("Saved"); } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => { try { await adminApi.delete(`/rules/${id}`); load(); } catch { toast.error("Delete failed"); } };
  const move = async (idx, dir) => {
    const next = [...groups]; const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setGroups(next);
    try { await adminApi.post("/rules/reorder", next.map((g) => g.id)); } catch { toast.error("Reorder failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-[#8F877C] text-sm">Add categories and rules, reorder and edit them.</p>
        <button onClick={add} data-testid="rules-add-btn" className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-4 py-2.5 hover:bg-bone transition-colors"><Plus className="h-4 w-4" /> Add Category</button>
      </div>
      {groups.map((g, idx) => (
        <div key={g.id} data-testid={`rules-edit-${idx}`} className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Title" value={g.cat} onChange={(v) => patch(g.id, "cat", v)} />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Sidebar category</p>
              <select value={g.category || ""} onChange={(e) => patch(g.id, "category", e.target.value)} data-testid={`rules-category-${idx}`}
                className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 text-sm text-bone focus:outline-none focus:border-teal">
                <option value="">Auto (by title)</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] leading-relaxed">
              Each box is one rule. Inside a rule: press <span className="text-teal">Enter twice</span> (blank line) for spacing / a new paragraph, and start a line with <span className="text-teal">- </span> for a bullet point.
              Start a whole box with <span className="text-teal">##</span> for a sub-heading, or <span className="text-teal">&gt;</span> for a plain paragraph (no number).
            </p>
            {g.items.map((item, i) => (
              <div key={i} className="flex gap-2">
                <textarea value={item} onChange={(e) => patchItem(g.id, i, e.target.value)} rows={item && item.includes("\n") ? Math.min(item.split("\n").length + 1, 14) : 2}
                  data-testid={`rules-item-${idx}-${i}`}
                  className="flex-1 bg-obsidian border border-[#2A201A] px-3 py-2 text-sm leading-relaxed text-bone focus:outline-none focus:border-teal resize-y font-body" />
                <button onClick={() => removeItem(g.id, i)} className="p-2 self-start border border-[#2A201A] text-[#8F877C] hover:border-destructive hover:text-destructive transition-colors"><X className="h-4 w-4" /></button>
              </div>
            ))}
            <button onClick={() => addItem(g.id)} className="font-feature text-xs uppercase tracking-widest text-teal hover:text-bone transition-colors">+ Add rule</button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => save(g)} className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal/90 text-obsidian px-4 py-2 hover:bg-teal"><Save className="h-4 w-4" /> Save</button>
            <button onClick={() => move(idx, -1)} className="p-2 border border-[#2A201A] hover:border-teal hover:text-teal transition-colors"><ChevronUp className="h-4 w-4" /></button>
            <button onClick={() => move(idx, 1)} className="p-2 border border-[#2A201A] hover:border-teal hover:text-teal transition-colors"><ChevronDown className="h-4 w-4" /></button>
            <button onClick={() => remove(g.id)} className="ml-auto p-2 border border-[#2A201A] text-[#8F877C] hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
};
